import dayjs from '../lib/dayjs';
import type { ColumnFiltersState, FilterFn, RowData } from '@tanstack/react-table';
import { rankItem, rankings } from '@tanstack/match-sorter-utils';
import { ValueType } from '../types/ValueType';
import {
    DataTableFilterKind,
    DataTableFilterOperation,
    DataTableGlobalSearchMatch,
    IDataTableColumnFilter,
    IDataTableFilterValue,
} from './DataTable.types';
import { formatCellText } from './dataTableFormat';
import type { DataTableFeatures, DataTableRow } from './dataTableFeatures';

/** Базовый вид фильтра по типу данных. */
export function filterKindOf(dataType?: ValueType): DataTableFilterKind {
    switch (dataType) {
        case ValueType.Money:
        case ValueType.Double:
        case ValueType.Integer:
            return 'number';
        case ValueType.Date:
        case ValueType.DateTime:
        case ValueType.NoSecDateTime:
            return 'date';
        case ValueType.Boolean:
            return 'boolean';
        default:
            return 'text';
    }
}

/**
 * Значение фильтра даты со временем (ISO `2026-10-04T15:00…`). Дата без времени
 * сравнивается по дню, время — с точностью записи (минута или секунда, см. dateFilterSpan).
 */
export const hasTimePart = (value: unknown): boolean =>
    typeof value === 'string' && /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(value);

/** Значение фильтра с временем до секунд (`…T12:30:15`), а не до минут. */
export const hasSecondsPart = (value: unknown): boolean =>
    typeof value === 'string' && /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}/.test(value);

/**
 * Интервал, который означает значение фильтра даты, — единица его точности: дата без времени — день, время до
 * минут — минута, до секунд — секунда. `start`/`end` — миллисекунды, `startIso`/`endIso` — те же границы для
 * сервера (конец — в записи значения: та же точность и зона). Значение с долями секунды (условие из кода) — точный
 * момент, интервала нет: null, как и у невалидного.
 */
export interface IDateFilterSpan {
    start: number;
    end: number;
    startIso: string;
    endIso: string;
}

const MOMENT_RE = /^(\d{4})-(\d\d)-(\d\d)([T ])(\d\d):(\d\d)(?::(\d\d))?/;
const pad = (n: number, width = 2) => String(n).padStart(width, '0');

export function dateFilterSpan(value: unknown): IDateFilterSpan | null {
    if (typeof value !== 'string') return null;
    if (!hasTimePart(value)) {
        const day = dayjs(value).startOf('day');
        if (!day.isValid()) return null;
        const next = day.add(1, 'day');
        return {
            start: day.valueOf(),
            end: next.valueOf(),
            startIso: day.format('YYYY-MM-DD'),
            endIso: next.format('YYYY-MM-DD'),
        };
    }
    const m = MOMENT_RE.exec(value);
    const start = dayjs(value).valueOf();
    if (!m || Number.isNaN(start)) return null;
    const [whole, y, mo, d, sep, hh, mm, ss] = m;
    const zone = value.slice(whole.length);
    if (zone.startsWith('.')) return null;
    const unit = ss === undefined ? 60_000 : 1000;
    // Конец считается по записанным часам, а не в зоне браузера: зона значения сохраняется, переход на летнее
    // время не сдвигает границу.
    const e = new Date(Date.UTC(+y, +mo - 1, +d, +hh, +mm, ss === undefined ? 0 : +ss) + unit);
    const endIso =
        `${pad(e.getUTCFullYear(), 4)}-${pad(e.getUTCMonth() + 1)}-${pad(e.getUTCDate())}` +
        `${sep}${pad(e.getUTCHours())}:${pad(e.getUTCMinutes())}` +
        `${ss === undefined ? '' : `:${pad(e.getUTCSeconds())}`}${zone}`;
    return { start, end: start + unit, startIso: value, endIso };
}

/** Операции по виду фильтра — полный набор DevExtreme filterRow (первая = дефолт). */
export const FILTER_OPS: Record<DataTableFilterKind, string[]> = {
    text: ['contains', 'notcontains', 'startswith', 'endswith', '=', '<>'],
    number: ['=', '<>', '<', '>', '<=', '>=', 'between'],
    date: ['=', '<>', '<', '>', '<=', '>=', 'between'],
    boolean: ['='],
};

/** Операции строки фильтров колонки: набор вида, суженный списком колонки. Порядок — набора,
 *  чтобы меню выглядело одинаково во всех колонках; список без подходящих операций не сужает. */
export const filterOpsOf = (
    kind: DataTableFilterKind,
    allowed?: readonly DataTableFilterOperation[],
): readonly string[] => {
    const all = FILTER_OPS[kind];
    if (!allowed?.length) return all;
    const ops = all.filter((op) => (allowed as readonly string[]).includes(op));
    return ops.length ? ops : all;
};

/** Операция нового условия колонки: заданная, если она доступна, иначе первая доступная. */
export const defaultFilterOpOf = (ops: readonly string[], preferred?: DataTableFilterOperation): string =>
    preferred && ops.includes(preferred) ? preferred : ops[0];

/** Подписи операций — как operationDescriptions DevExtreme. */
export const OP_LABEL: Record<string, string> = {
    contains: 'Содержит',
    notcontains: 'Не содержит',
    startswith: 'Начинается с',
    endswith: 'Заканчивается на',
    '=': 'Равно',
    '<>': 'Не равно',
    '<': 'Меньше',
    '>': 'Больше',
    '<=': 'Меньше или равно',
    '>=': 'Больше или равно',
    between: 'Между',
};

/** Символ операции на кнопке выбора: он и есть индикатор выбранного типа
 *  фильтра (`contains` показывается лупой — метафора поиска по вхождению). */
export const OP_SYMBOL: Record<string, string> = {
    notcontains: '⊄',
    startswith: 'a…',
    endswith: '…a',
    '=': '=',
    '<>': '≠',
    '<': '<',
    '>': '>',
    '<=': '≤',
    '>=': '≥',
    between: '⇿',
};

export const isFilterValueEmpty = (v: IDataTableFilterValue | undefined): boolean => {
    if (v == null) return true;
    if (Array.isArray(v.value)) return v.value[0] === '' && v.value[1] === '';
    return v.value === '';
};

const cmp = (a: number, op: string, b: number): boolean => {
    switch (op) {
        case '=':
            return a === b;
        case '<>':
            return a !== b;
        case '<':
            return a < b;
        case '>':
            return a > b;
        case '<=':
            return a <= b;
        case '>=':
            return a >= b;
        default:
            return false;
    }
};

/* Семантика совпадает с серверной (buildServerLoadOptions, calculateFilterExpression DX):
 * null/пустая ячейка не проходит ни одну операцию (как NULL в SQL-сравнении),
 * дата — интервал своей точности (dateFilterSpan): без времени — день целиком (`<= d` — по конец дня,
 * `> d` — со следующего, «между» включает оба дня), `12:30` — минута, `12:30:15` — секунда, доли секунды —
 * точный момент; boolean-фильтр «Нет» пропускает и null
 * (серверное [field,'<>',1]). Строки — регистронезависимо, как ILIKE. */
export function dxMatch(
    kind: DataTableFilterKind,
    cell: unknown,
    op: string,
    value: string | [string, string],
): boolean {
    if (kind === 'boolean') {
        const truthy = cell === true || cell === 1 || cell === '1' || cell === 'true';
        return value === 'true' ? truthy : !truthy;
    }

    if (cell == null || cell === '') return false;

    if (kind === 'number') {
        const num = Number(cell);
        if (Number.isNaN(num)) return false;
        if (op === 'between') {
            const [from, to] = value as [string, string];
            // одна заполненная граница работает сама по себе — как на серверном пути
            if (from !== '' && num < Number(from)) return false;
            if (to !== '' && num > Number(to)) return false;
            return true;
        }
        return cmp(num, op, Number(value));
    }

    if (kind === 'date') {
        const cellDate = dayjs(String(cell));
        if (!cellDate.isValid()) return false;
        const ts = cellDate.valueOf();
        if (op === 'between') {
            const [from, to] = value as [string, string];
            // одна заполненная граница работает сама по себе — как на серверном пути: «с» — это >=, «по» — <=
            // (по конец интервала своей точности)
            if (from !== '' && !dxMatch(kind, cell, '>=', from)) return false;
            if (to !== '' && !dxMatch(kind, cell, '<=', to)) return false;
            return true;
        }
        const span = dateFilterSpan(value);
        if (!span) {
            const moment = hasTimePart(value) ? dayjs(String(value)).valueOf() : NaN;
            return !Number.isNaN(moment) && cmp(ts, op, moment);
        }
        switch (op) {
            case '=':
                return ts >= span.start && ts < span.end;
            case '<>':
                return ts < span.start || ts >= span.end;
            case '<=':
                return ts < span.end;
            case '>':
                return ts >= span.end;
            default:
                return cmp(ts, op, span.start);
        }
    }

    const str = String(cell).toLowerCase();
    const q = String(value).toLowerCase();
    switch (op) {
        case 'contains':
            return str.includes(q);
        case 'notcontains':
            return !str.includes(q);
        case 'startswith':
            return str.startsWith(q);
        case 'endswith':
            return str.endsWith(q);
        case '=':
            return str === q;
        case '<>':
            return str !== q;
        default:
            return false;
    }
}

export const isColumnFilterEmpty = (fv: IDataTableColumnFilter | undefined): boolean =>
    (fv?.row == null || isFilterValueEmpty(fv.row)) && !fv?.header?.length;

/** Фильтры с изменённой частью (строка фильтров или header-фильтр) одной колонки; пустой фильтр колонки убирается. */
export function patchColumnFilters(
    filters: ColumnFiltersState,
    id: string,
    patch: Partial<IDataTableColumnFilter>,
): ColumnFiltersState {
    const existing = filters.find((f) => f.id === id)?.value as IDataTableColumnFilter | undefined;
    const next: IDataTableColumnFilter = { ...existing, ...patch };
    const rest = filters.filter((f) => f.id !== id);
    return isColumnFilterEmpty(next) ? rest : [...rest, { id, value: next }];
}

/** Фильтры для значений воронки колонки: снят только выбор в самой воронке, строка фильтров колонки и порядок
 *  условий остаются (семантика клиентской воронки). */
export function withoutHeaderFilter(filters: ColumnFiltersState, id: string): ColumnFiltersState {
    const index = filters.findIndex((f) => f.id === id);
    const own = filters[index]?.value as IDataTableColumnFilter | undefined;
    if (!own?.header?.length) return filters;
    const next = [...filters];
    if (own.row && !isFilterValueEmpty(own.row)) next[index] = { id, value: { row: own.row } };
    else next.splice(index, 1);
    return next;
}

/** Фабрика filterFn колонки: filter-row (DX-семантика) И header-фильтр по значениям. */
export function makeColumnFilterFn<T extends RowData>(
    kind: DataTableFilterKind,
    dataType?: ValueType,
    cellText?: (row: T) => string,
): FilterFn<DataTableFeatures, T> {
    return (row, columnId, filterValue: IDataTableColumnFilter) => {
        if (filterValue?.row && !isFilterValueEmpty(filterValue.row)) {
            if (!dxMatch(kind, row.getValue(columnId), filterValue.row.op, filterValue.row.value)) return false;
        }
        if (filterValue?.header?.length) {
            // отбор идёт по видимому тексту — тому же, что показан в списке значений
            const text = cellText ? cellText(row.original) : formatCellText(row.getValue(columnId), dataType);
            if (!filterValue.header.includes(text === '' ? null : text)) return false;
        }
        return true;
    };
}

/** Ступени глобального поиска, от строгой к мягкой; таблица берёт первую,
 *  на которой нашлась хоть одна строка:
 *  - `phrase` — запрос целиком подстрокой в одной колонке;
 *  - `words` — каждое слово подстрокой в любой колонке, в любом порядке;
 *  - `similar` — буквы слова по порядку вразброс (match-sorter MATCHES: «мигр»
 *    находит «Документ/ИД договора»). Только запасная ступень: на длинных
 *    значениях короткий запрос так проходит почти в любой строке. */
export type GlobalSearchMode = 'phrase' | 'words' | 'similar';

function globalSearchTerms(query: string, mode: GlobalSearchMode): string[] {
    const trimmed = query.trim();
    if (trimmed === '') return [];
    return mode === 'phrase' ? [trimmed] : trimmed.split(/\s+/);
}

/** Строка (её значения по колонкам) подходит под запрос на ступени `mode`;
 *  регистр не важен. */
export function matchesGlobalSearch(
    values: readonly unknown[],
    query: string,
    mode: GlobalSearchMode = 'phrase',
): boolean {
    const threshold = mode === 'similar' ? rankings.MATCHES : rankings.CONTAINS;
    const texts = values.filter((v) => v != null).map((v) => String(v));
    return globalSearchTerms(query, mode).every((term) =>
        texts.some((text) => rankItem(text, term, { threshold }).passed),
    );
}

interface IGlobalSearchRun {
    query: string;
    mode: GlobalSearchMode;
    columnIds: string[];
    hits: WeakMap<object, boolean>;
}

export interface IGlobalSearch<T extends RowData> {
    filterFn: FilterFn<DataTableFeatures, T>;
    /** Ступень последнего прогона фильтрации по этому запросу. */
    modeOf(query: string): GlobalSearchMode;
}

/** Глобальный поиск по строке целиком: слова могут найтись в разных колонках.
 *  Экземпляр держать стабильным на всю жизнь таблицы — он помнит ступень прогона.
 *  `contains` — без запасной ступени `similar`: поиск остаётся подстрочным. */
export function createGlobalSearch<T extends RowData>(match: DataTableGlobalSearchMatch = 'fuzzy'): IGlobalSearch<T> {
    let run: IGlobalSearchRun | undefined;

    const startRun = (table: DataTableRow<T>['table'], query: string): IGlobalSearchRun => {
        const columnIds = table
            .getAllLeafColumns()
            .filter((c) => c.getCanGlobalFilter())
            .map((c) => c.id);
        const columnChecks = (table.atoms.columnFilters?.get() ?? []).flatMap((f) => {
            const fn = table.getColumn(f.id)?.getFilterFn();
            return fn ? [{ id: f.id, fn, value: fn.resolveFilterValue?.(f.value) ?? f.value }] : [];
        });
        // ступень выбирается по строкам, оставленным фильтрами колонок: иначе
        // найденное за фильтром не дало бы перейти к следующей
        const candidates = table
            .getPreFilteredRowModel()
            .flatRows.filter((r) => columnChecks.every((c) => c.fn(r, c.id, c.value, () => undefined)))
            .map((r) => columnIds.map((id) => r.getValue(id)));
        const steps: GlobalSearchMode[] = /\s/.test(query.trim()) ? ['phrase', 'words'] : ['phrase'];
        const mode =
            steps.find((m) => candidates.some((values) => matchesGlobalSearch(values, query, m))) ??
            (match === 'contains' ? steps[steps.length - 1] : 'similar');
        return { query, mode, columnIds, hits: new WeakMap() };
    };

    const filterFn: FilterFn<DataTableFeatures, T> = (row, _columnId, query: string) => {
        run ??= startRun(row.table, query);
        // таблица спрашивает по каждой колонке, пока одна не ответит «да», — а
        // ответ один на строку
        let hit = run.hits.get(row);
        if (hit === undefined) {
            hit = matchesGlobalSearch(
                run.columnIds.map((id) => row.getValue(id)),
                run.query,
                run.mode,
            );
            run.hits.set(row, hit);
        }
        return hit;
    };
    // таблица зовёт resolveFilterValue в начале каждого прогона фильтрации,
    // до первой строки: данные, фильтры колонок или запрос сменились — режим
    // считается заново
    filterFn.resolveFilterValue = (query: string) => {
        run = undefined;
        return query;
    };

    return {
        filterFn,
        modeOf: (query) => (run?.query === query ? run.mode : 'phrase'),
    };
}

/** Подпись пустого значения в воронке и чипах фильтра. */
export const EMPTY_FACET_LABEL = '(Пустые)';

export const formatFilterValue = (value: unknown, kind: DataTableFilterKind): string => {
    if (value === null || value === '') return EMPTY_FACET_LABEL;
    if (kind === 'date') {
        const date = dayjs(String(value));
        if (date.isValid()) {
            return date.format(
                hasTimePart(value) ? (date.second() ? 'DD.MM.YYYY HH:mm:ss' : 'DD.MM.YYYY HH:mm') : 'DD.MM.YYYY',
            );
        }
    }
    if (kind === 'boolean') return value === 'true' || value === true ? 'да' : 'нет';
    return String(value);
};

/** Текст условия колонки: «содержит «abc»», «с 01.01.2026 по 31.01.2026», «A, B».
 *  `headerLabel` — подпись значения header-фильтра по ключу (из списка значений воронки). */
export const describeColumnFilter = (
    filter: IDataTableColumnFilter,
    kind: DataTableFilterKind,
    headerLabel?: (key: string) => string | undefined,
): string => {
    const parts: string[] = [];
    const row = filter.row;
    if (row && row.value !== '' && row.value != null) {
        if (Array.isArray(row.value)) {
            const [from, to] = row.value;
            const range = [
                from && `с ${formatFilterValue(from, kind)}`,
                to && `по ${formatFilterValue(to, kind)}`,
            ].filter(Boolean);
            if (range.length) parts.push(range.join(' '));
        } else {
            const label = (OP_LABEL[row.op] ?? row.op).toLocaleLowerCase('ru-RU');
            const text = formatFilterValue(row.value, kind);
            parts.push(`${label} ${kind === 'text' ? `«${text}»` : text}`);
        }
    }
    if (filter.header?.length) {
        parts.push(
            filter.header
                .map((v) => (v === null ? undefined : headerLabel?.(v)) ?? formatFilterValue(v, kind))
                .join(', '),
        );
    }
    return parts.join('; ');
};
